import React, { useState, useEffect, useCallback } from "react";
import { LayoutListhooks } from "../../Helper/LayoutHelper";
import { Link } from "react-router-dom";
import {
    hasCreatePermission,
    hasEditPermission,
    hasdeletePermission,
    hasSharePermission,
} from "../../Utils/PermissionUtils";
import ShareModal from "../../Utils/ShareModal";
import DeleteModal from "../../Utils/DeleteModal";
import { toast } from "react-toastify";
import service from "../../Services/service";

const LayoutListComponent = () => {
    const [layouts, setLayouts] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("Name");
    const [sortDescending, setSortDescending] = useState(false);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [showShareModal, setShowShareModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedLayout, setSelectedLayout] = useState(null);

    const canEditLayout = hasEditPermission("Layout");
    const canDeleteLayout = hasdeletePermission("Layout");
    const canShareLayout = hasSharePermission("Layout");
    const canCreateLayout = hasCreatePermission("Layout");

    const fetchLayouts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await LayoutListhooks(
                searchTerm,
                sortBy,
                sortDescending,
                pageNumber,
                pageSize
            );

            if (data) {
                setLayouts(data.items || []);
                setTotalPages(data.totalPages || 1);
                setTotalItems(data.totalItems || 0);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to load layouts.");
        } finally {
            setLoading(false);
        }
    }, [searchTerm, sortBy, sortDescending, pageNumber, pageSize]);

    useEffect(() => {
        fetchLayouts();
    }, [fetchLayouts]);

    const handleSortChange = (e) => {
        const [field, order] = e.target.value.split("_");
        setSortBy(field);
        setSortDescending(order === "desc");
        setPageNumber(1);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedLayout) return;

        try {
            await service.getdata(`Layout/DeleteById?id=${selectedLayout.layoutId}`);
            toast.success("Layout deleted successfully!");
            fetchLayouts();
        } catch {
            toast.error("Error deleting layout.");
        } finally {
            setShowDeleteModal(false);
        }
    };

    return (
        <div className="container-fluid content-top-gap">

            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb my-breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active">Layout Management</li>
                </ol>
            </nav>

            <div className="card card_border shadow-sm">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">

                        <div>
                            <h3 className="mb-1 font-weight-bold">
                                Layout Management
                            </h3>

                            <small className="text-muted">
                                Create and manage digital signage layouts
                            </small>
                        </div>

                        {canCreateLayout && (
                            <Link
                                to="/CreateLayout"
                                className="btn btn-outline-primary btn-sm"
                            >
                                <i className="fa fa-plus mr-2"></i>
                                New Layout
                            </Link>
                        )}

                    </div>
                </div>

                <div className="card-body">

                    {/* Filters */}
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <input
                                className="form-control"
                                placeholder="Search by name..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPageNumber(1);
                                }}
                            />
                        </div>

                        <div className="col-md-4 mt-2 mt-md-0">
                            <select
                                className="custom-select"
                                onChange={handleSortChange}
                                value={`${sortBy}_${sortDescending ? "desc" : "asc"}`}
                            >
                                <option value="Name_asc">Name (A-Z)</option>
                                <option value="Name_desc">Name (Z-A)</option>
                                <option value="CreatedAt_desc">Newest</option>
                                <option value="CreatedAt_asc">Oldest</option>
                                <option value="UpdatedAt_desc">Recently Updated</option>
                                <option value="UpdatedAt_asc">Least Updated</option>
                            </select>
                        </div>

                        <div className="col-md-4 mt-2 mt-md-0">
                            <select
                                className="custom-select"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPageNumber(1);
                                }}
                            >
                                <option value={10}>Show 10</option>
                                <option value={20}>Show 20</option>
                                <option value={50}>Show 50</option>
                                <option value={100}>Show 100</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                            <thead className="bg-light">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Resolution</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="text-center">
                                            Loading layouts...
                                        </td>
                                    </tr>
                                )}

                                {!loading && layouts.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            No layouts found.
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    layouts.map((layout, index) => (
                                        <tr key={layout.layoutId}>
                                            <td>
                                                {(pageNumber - 1) * pageSize + index + 1}
                                            </td>
                                            <td>

                                                <div className="d-flex align-items-center">

                                                    <div className="layout-icon mr-3">
                                                        <i className="fa fa-desktop"></i>
                                                    </div>

                                                    <div>

                                                        <div className="font-weight-bold">
                                                            {layout.name}
                                                        </div>

                                                        <small className="text-muted">
                                                            {layout.layoutType}
                                                        </small>

                                                    </div>

                                                </div>

                                            </td>
                                            <td><span className="badge badge-info">
                                                {layout.resolutionType}
                                            </span></td>


                                            <td className="text-center">
                                                <div className="btn-group btn-group-sm">

                                                    {canEditLayout && (
                                                        <>
                                                            <Link
                                                                to={`/EditLayout/${layout.layoutId}`}
                                                                className="btn btn-outline-info"
                                                            >
                                                                Edit
                                                            </Link>

                                                            <Link
                                                                to={`/Design/${layout.layoutId}?name=${layout.name}`}
                                                                className="btn btn-outline-primary"
                                                            >
                                                                Design
                                                            </Link>
                                                        </>
                                                    )}

                                                    <Link
                                                        to={`/Demo/${layout.layoutId}`}
                                                        className="btn btn-outline-success"
                                                    >
                                                        Preview
                                                    </Link>

                                                    {canDeleteLayout && (<Link to="#" className="btn btn-outline-danger btn-sm"
                                                        onClick={() => {
                                                            setSelectedLayout(layout);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        title="Delete Layout"> Delete </Link>
                                                    )}
                                                    {canShareLayout && (<Link className="btn btn-outline-success btn-sm" title="Share"
                                                        onClick={() => {
                                                            setSelectedLayout(layout);
                                                            setShowShareModal(true);
                                                        }} > Share </Link>)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap mt-4">

                        <div className="text-muted">
                            Showing{" "}
                            <strong>
                                {(pageNumber - 1) * pageSize + 1}
                            </strong>
                            {" - "}
                            <strong>
                                {Math.min(pageNumber * pageSize, totalItems)}
                            </strong>
                            {" of "}
                            <strong>{totalItems}</strong>
                            {" layouts"}
                        </div>

                        <nav>
                            <ul className="pagination pagination-sm mb-0">

                                <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setPageNumber(pageNumber - 1)}
                                    >
                                        <i className="fa fa-angle-left"></i>
                                    </button>
                                </li>

                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li
                                        key={i}
                                        className={`page-item ${pageNumber === i + 1 ? "active" : ""
                                            }`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setPageNumber(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setPageNumber(pageNumber + 1)}
                                    >
                                        <i className="fa fa-angle-right"></i>
                                    </button>
                                </li>

                            </ul>
                        </nav>

                    </div>
                </div>
            </div>

            {/* Share Modal */}
            <ShareModal
                show={showShareModal}
                handleClose={() => setShowShareModal(false)}
                screenName={selectedLayout?.name}
                moduleId={1}
                rowId={selectedLayout?.layoutId}
            />

            {/* Delete Modal */}
            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleConfirm={handleDeleteConfirm}
                message="Are you sure you want to delete this layout?"
                itemName={`${selectedLayout?.name}`}
            />
        </div>
    );
};

export default LayoutListComponent;
